import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { Project } from '@/src/renderer/lib/db/models'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon'
import './MoveProjectDropdown.scss'

export interface MoveProjectDropdownProps {
  lifeSpace: 'personal' | 'work' | 'home'
  onSelect: (project: Project) => void
}

/**
 * Dropdown-Inhalt für die "Verschieben"-Action. Zeigt alle Projekte des
 * aktuellen Lebensbereichs (ohne System-Projekte wie Quick Notes) und ruft
 * `onSelect` mit dem gewählten Projekt auf.
 */
export function MoveProjectDropdown({ lifeSpace, onSelect }: MoveProjectDropdownProps) {
  const { t } = useTranslation()
  const projects = useDataSource().useProjectsByLifeSpace(lifeSpace)
  const loading = projects === undefined

  const userProjects = useMemo(
    () =>
      (projects ?? [])
        .filter(p => !p.isSystem && !p.deletedAt && p.status !== 'archived')
        .sort((a, b) => a.order - b.order),
    [projects],
  )

  if (loading) {
    return (
      <div className="move-project-dropdown">
        <div className="move-project-dropdown__empty">
          {t('quickNotes.clarify.movePickerLoading')}
        </div>
      </div>
    )
  }

  if (userProjects.length === 0) {
    return (
      <div className="move-project-dropdown">
        <div className="move-project-dropdown__empty">
          {t('quickNotes.clarify.movePickerEmpty')}
        </div>
      </div>
    )
  }

  return (
    <div className="move-project-dropdown" role="menu">
      <div className="move-project-dropdown__title">
        {t('quickNotes.clarify.movePickerTitle')}
      </div>
      {userProjects.map(project => (
        <button
          key={project.id}
          type="button"
          role="menuitem"
          className="move-project-dropdown__item"
          onClick={() => onSelect(project)}
        >
          <span className="move-project-dropdown__icon" aria-hidden="true">
            <ProjectIcon icon={project.icon} iconColor={project.iconColor} size={16} />
          </span>
          <span className="move-project-dropdown__name">{project.name}</span>
        </button>
      ))}
    </div>
  )
}
